import { Point } from '@view-model/models/common/basic';
import type { StickyZone } from '@view-model/models/sticky/StickyZoneView/domain';
import type { RectShape } from '@model-framework/shape';
import { StickyZoneResizer } from '@view-model/models/sticky/StickyZoneView/components';
import { useMemo } from 'react';
import { useOtherUserIsSelected } from '@view-model/models/sticky/client-selected-items/useOtherUserIsSelected';
import { getClientId } from '@framework/app';
import type { ModelId } from 'balus-common';
import { EditingUserIconView } from '@model-framework/text/editing-user/EditingUserIconView';
import { CreatedUserView } from '@view-model/models/framework/ui/CreatedUserView';
import { useNodeEditingUser } from '../editing-users/useNodeEditingUser';
import { StickyZoneBasicBorder, StickyZoneContainerBorder } from './components';
import { CONTAINER_MARGIN } from './constants';

type Props = {
  isMultiSelected: boolean;
  isHoverSelected: boolean;
  isSelected: boolean;
  isLinkableTarget: boolean;
  readonly: boolean;
  modelId: ModelId;
  stickyZone: StickyZone;
  position: Point;
  shape: RectShape;
  showCreatedUser: boolean;
};

export const StickyZoneBorderView: React.FC<Props> = ({
  isMultiSelected,
  isHoverSelected,
  isSelected,
  isLinkableTarget,
  readonly,
  modelId,
  stickyZone,
  position,
  shape,
  showCreatedUser,
}: Props) => {
  const clientId = useMemo(() => getClientId(), []);
  const otherUserSelected = useOtherUserIsSelected({
    nodeId: stickyZone.id,
    clientId,
    modelId,
  });

  const { height } = stickyZone.size.dump();

  const editingUser = useNodeEditingUser(stickyZone.key);

  const { borderColor, hoverBorderColor } = stickyZone.style.getStyles();

  const containerRectShape = useMemo(
    () =>
      shape.resize({
        dWidth: CONTAINER_MARGIN * 2,
        dHeight: CONTAINER_MARGIN * 2,
        rx: 20,
        ry: 20,
      }),
    [shape]
  );

  // 共通のpropsを定義
  const basicBorderProps = {
    position,
    shape,
    zoneShape: stickyZone.style.shape,
    strokeColor: borderColor,
    isHoverSelected,
    hoverBorderColor,
  };

  const containerBorderProps = {
    position,
    containerRectShape,
  };

  return (
    <g className="pointer-events-none">
      {isMultiSelected ? (
        <>
          <StickyZoneBasicBorder {...basicBorderProps} />
          <StickyZoneContainerBorder className="fill-black/0 stroke-brand" {...containerBorderProps} />
        </>
      ) : isSelected ? (
        !readonly ? (
          <StickyZoneResizer stickyZone={stickyZone} position={position} />
        ) : (
          // 編集不可の場合には、ブランドカラーの枠線を表示する
          <>
            <StickyZoneBasicBorder {...basicBorderProps} />
            <StickyZoneContainerBorder className="fill-black/0 stroke-brand" {...containerBorderProps} />
          </>
        )
      ) : otherUserSelected ? (
        // 自分が選択していなくて、他の人が選択している場合には、黄色の枠線を表示する
        <>
          <StickyZoneBasicBorder {...basicBorderProps} />
          <StickyZoneContainerBorder
            className="fill-black/0 stroke-orange-400"
            strokeWidth={10}
            {...containerBorderProps}
          />
        </>
      ) : (
        <StickyZoneBasicBorder {...basicBorderProps} />
      )}

      {isLinkableTarget && (
        <g
          transform={Point.fromPosition({
            x: position.x - CONTAINER_MARGIN,
            y: position.y - CONTAINER_MARGIN,
          }).toSVGTranslate()}
        >
          {containerRectShape.render({
            strokeWidth: 10,
            pointerEvents: 'none',
            className: 'stroke-indigo-300 fill-black/0',
          })}
        </g>
      )}

      {editingUser && (
        <g transform={Point.fromPosition(position).toSVGTranslate()}>
          {shape.render({ pointerEvents: 'none', className: 'fill-neutral-500/20' })}
        </g>
      )}

      {showCreatedUser && !editingUser && (
        <CreatedUserView
          userId={stickyZone.createdUserId}
          position={Point.fromPosition({
            x: position.x,
            y: position.y + height,
          })}
        />
      )}

      {editingUser && (
        <EditingUserIconView
          currentEditingUser={editingUser}
          position={Point.fromPosition({
            x: position.x,
            y: position.y + height,
          })}
          iconSize={48}
        />
      )}
    </g>
  );
};
